import { inject, Injectable } from '@angular/core';
import {
  IWatchList,
  NetworkDeviceTopic,
  UbieTopic,
} from '@ubie/mqttcommunication';

import { Functionblock } from '../../../services/functionblocks/functionblocks.interfaces';
import { NetworkDevice } from '../../networkdevices/networkdevices.interfaces';
import { CommunicationCoreService } from '../communication-core/communication-core.service';

@Injectable({
  providedIn: 'root',
})
export class CommunicationFilterService {
  private readonly communicationCoreService: CommunicationCoreService = inject(
    CommunicationCoreService,
  );

  currentFilter: IWatchList = {
    ubies: [],
  };

  private resetCurrentFilter(): void {
    this.currentFilter = {
      ubies: [],
    };
  }

  /**
   * adds the functionblocks to the filter in the communication core that allows events to pass through
   * @param functionBlocks the functionblocks to enable in the filter
   * @returns a promise that you can wait for
   */
  enableFunctionBlocks(functionBlocks: Functionblock[]): Promise<void> {
    this.resetCurrentFilter();
    const isMade = this.createFilter(functionBlocks);

    if (isMade) {
      return this.communicationCoreService.addToWatchList(this.currentFilter);
    }

    return Promise.resolve();
  }

  /**
   * resets the filter and then adds the functionblocks to the filter in the communication core that allows events to pass through
   * @param functionBlocks the functionblocks to enable in the filter
   * @returns a promise that you can wait for
   */
  enableFunctionBlocksWithReplace(
    functionBlocks: Functionblock[],
  ): Promise<void> {
    this.resetCurrentFilter();
    const isMade = this.createFilter(functionBlocks);

    if (isMade) {
      return this.communicationCoreService.replaceWatchList(this.currentFilter);
    }

    return Promise.resolve();
  }

  /**
   * removes the functionblocks from the filter in the communication core that allows events to pass through
   * @param functionBlocks the functionblocks to disable in the filter
   * @returns a promise that you can wait for
   */
  disableFunctionBlocks(functionBlocks: Functionblock[]): Promise<void> {
    this.resetCurrentFilter();
    const isMade = this.createFilter(functionBlocks);

    if (isMade) {
      return this.communicationCoreService.removeFromWatchList(
        this.currentFilter,
      );
    }
    return Promise.resolve();
  }

  /**
   *  adds the networkdevices to the filter in the communication core that allows events to pass through
   * @param networkDevices the networkdevices to enable in the filter
   * @returns a promise that you can wait for
   **/
  enableNetworkDevices(networkDevices: NetworkDevice[]): Promise<void> {
    this.resetCurrentFilter();

    const isMade = this.createNetworkDeviceFilter(networkDevices);
    if (isMade) {
      return this.communicationCoreService.addToWatchList(this.currentFilter);
    }

    return Promise.resolve();
  }

  /**
   *   removes the networkdevices from the filter in the communication core that allows events to pass through
   * @param networkDevices the networkdevices to disable in the filter
   * @returns a promise that you can wait for
   *
   **/
  disableNetworkDevices(networkDevices: NetworkDevice[]): Promise<void> {
    this.resetCurrentFilter();
    const isMade = this.createNetworkDeviceFilter(networkDevices);

    if (isMade) {
      return this.communicationCoreService.removeFromWatchList(
        this.currentFilter,
      );
    }

    return Promise.resolve();
  }

  /**
   *  adds the ubies to the filter in the communication core that allows events to pass through
   * @param ubies the ubies to enable
   * @returns  a promise that you can wait for
   */
  enableUbieDevices(ubieSerialNumbers: string[]): Promise<void> {
    if (ubieSerialNumbers.length > 0) {
      this.resetCurrentFilter();
      const isMade = this.createUbieFilter(ubieSerialNumbers);
      if (isMade) {
        return this.communicationCoreService.addToWatchList(this.currentFilter);
      }
    }

    return Promise.resolve();
  }

  /**
   *  removes the ubies from the filter in the communication core that allows events to pass through
   * @param ubieSerialNumbers the ubies to disable
   * @returns  a promise that you can wait for
   */
  disableUbieDevices(ubieSerialNumbers: string[]): Promise<void> {
    if (ubieSerialNumbers.length > 0) {
      this.resetCurrentFilter();
      const isMade = this.createUbieFilter(ubieSerialNumbers);

      if (isMade) {
        return this.communicationCoreService.removeFromWatchList(
          this.currentFilter,
        );
      }
    }

    return Promise.resolve();
  }

  clearFilter(): Promise<void> {
    return this.communicationCoreService.replaceWatchList({
      ubies: [],
    });
  }

  private createUbieFilter(ubieSerialNumbers: string[]): boolean {
    if (ubieSerialNumbers.length > 0) {
      ubieSerialNumbers.forEach((ubieSerial) => {
        this.upsertUbieTopic(ubieSerial);
      });

      return true;
    }
    return false;
  }

  private createNetworkDeviceFilter(networkDevices: NetworkDevice[]): boolean {
    //init filter

    if (networkDevices.length > 0) {
      networkDevices.forEach((networkDevice) => {
        if (networkDevice.device && networkDevice.serialNumber) {
          const ubieTopic = this.upsertUbieTopic(
            networkDevice.device?.serialNumber,
          );
          this.findNetworkDeviceOrAdd(ubieTopic, networkDevice.serialNumber);
        }
      });

      return true;
    }
    return false;
  }

  private createFilter(functionBlocks: Functionblock[]): boolean {
    //init filter

    if (functionBlocks.length > 0) {
      functionBlocks.forEach((fb) => {
        if (fb.device) {
          const ubieTopic = this.upsertUbieTopic(fb.device?.serialNumber);
          this.findNetworkDeviceOrAddWithFunctionBlock(ubieTopic, fb);
        }
      });

      return true;
    }
    return false;
  }

  private findUbieTopic(ubieId: string): UbieTopic | undefined {
    return this.currentFilter.ubies.find((u) => u.ubieId === ubieId);
  }

  private findNetworkDeviceTopic(
    ubieTopic: UbieTopic,
    networkDeviceSerial: string,
  ): NetworkDeviceTopic | undefined {
    return ubieTopic.devices.find(
      (u) => u.networkDeviceId === networkDeviceSerial,
    );
  }

  private upsertUbieTopic(ubieId: string): UbieTopic {
    //check if the ubie exists in the current filter
    const ubie = this.findUbieTopic(ubieId);

    if (ubie) {
      return ubie;
    }

    const ubieTopic = {
      ubieId,
      devices: [],
    } as UbieTopic;

    this.currentFilter.ubies = [...this.currentFilter.ubies, ubieTopic];
    return ubieTopic;
  }

  private findNetworkDeviceOrAdd(
    ubieTopic: UbieTopic,
    networkDeviceSerialNumber: string,
  ): void {
    if (!ubieTopic.devices) {
      return;
    }

    const networkDeviceTopic: NetworkDeviceTopic | undefined =
      this.findNetworkDeviceTopic(ubieTopic, networkDeviceSerialNumber);

    if (!networkDeviceTopic) {
      const networkDeviceTopic = {
        networkDeviceId: networkDeviceSerialNumber,
        functionBlocks: [],
      } as NetworkDeviceTopic;

      ubieTopic.devices.push(networkDeviceTopic);
    }
  }

  private findNetworkDeviceOrAddWithFunctionBlock(
    ubieTopic: UbieTopic,
    functionblock: Functionblock,
  ): void {
    if (!functionblock.networkDevice) {
      return;
    }

    const networkDeviceTopic: NetworkDeviceTopic | undefined =
      this.findNetworkDeviceTopic(
        ubieTopic,
        functionblock.networkDevice.serialNumber,
      );

    if (!networkDeviceTopic) {
      const networkDeviceTopic = {
        networkDeviceId: functionblock.networkDevice?.serialNumber,
        functionBlocks: [functionblock.serialNumber],
      } as NetworkDeviceTopic;

      ubieTopic.devices.push(networkDeviceTopic);
    } else {
      networkDeviceTopic.functionBlocks.push(functionblock.serialNumber);
    }
  }
}
